import React, { useMemo } from "react";

// ── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  { id: "disable",       label: "Disable account",                    section: "Entra ID" },
  { id: "revoke",        label: "Revoke active sign-in sessions",      section: "Entra ID" },
  { id: "displayname",   label: "Update display name",                 section: "Entra ID" },
  { id: "clear",         label: "Clear job title & department",        section: "Entra ID" },
  { id: "manager",       label: "Remove manager",                      section: "Entra ID" },
  { id: "password",      label: "Reset password",                      section: "Entra ID" },
  { id: "authmethods",   label: "Remove authentication methods",       section: "Entra ID" },
  { id: "gal",           label: "Hide from Global Address List",       section: "Mailbox" },
  { id: "sharedmailbox", label: "Convert to shared mailbox",           section: "Mailbox" },
  { id: "mailboxaccess", label: "Grant mailbox access to delegates",   section: "Mailbox" },
  { id: "mailboxsize",   label: "Check mailbox storage",               section: "Mailbox" },
  { id: "groups",        label: "Remove group memberships & licensing",section: "Licensing" },
  { id: "onedrive",      label: "Grant OneDrive access to delegates",  section: "OneDrive" },
  { id: "email",         label: "Send notification email",             section: "Notification" },
];

const PATTERNS = {
  disable:       { success: /Account disabled/,                                  error: /ERROR.*Disable account/ },
  revoke:        { success: /sessions revoked/,                                  error: /ERROR.*Revoke sign-in/ },
  displayname:   { success: /Display name updated|already prefixed/,             error: /ERROR.*Update display name/ },
  clear:         { success: /Job title and department cleared/,                  error: /ERROR.*Clear job title/ },
  manager:       { success: /Manager removed|No manager was set/,                error: /ERROR.*Remove manager/ },
  password:      { success: /Password reset to a random value/,                  error: /ERROR.*Reset password/ },
  authmethods:   { success: /Authentication methods:/,                           error: null },
  gal:           { success: /Hidden from Global Address List/,                   error: /ERROR.*Hide from GAL/ },
  sharedmailbox: { success: /Convert to shared mailbox/,                         error: null },
  mailboxaccess: { success: /Grant mailbox access|mailbox access grant skipped/, error: null },
  mailboxsize:   { success: /Mailbox storage:|Mailbox size:/,                    error: null },
  groups:        { success: /Group removal:/,                                    error: /ERROR.*Fetch group/ },
  onedrive:      { success: /OneDrive:|OneDrive access grant skipped/,           error: /ERROR.*Grant OneDrive/ },
  email:         { success: /Notification email sent/,                           error: /sendNotificationEmail failed/ },
};

const SECTIONS = [...new Set(STEPS.map((s) => s.section))];

function parseStates(actionLog, isRunning) {
  const lines = (actionLog || "").split("\n");
  const states = {};

  for (const step of STEPS) {
    const { success, error } = PATTERNS[step.id];
    if (error && lines.some((l) => error.test(l))) {
      states[step.id] = "failed";
    } else if (success && lines.some((l) => success.test(l))) {
      states[step.id] = "completed";
    } else {
      states[step.id] = "pending";
    }
  }

  if (isRunning) {
    const first = STEPS.find((s) => states[s.id] === "pending");
    if (first) states[first.id] = "in-progress";
  }

  return states;
}

// ── Step circle icon ─────────────────────────────────────────────────────────
function StepIcon({ state }) {
  if (state === "completed") {
    return (
      <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#16a34a" />
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (state === "failed") {
    return (
      <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#dc2626" />
        <path d="M7 7L13 13M13 7L7 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === "in-progress") {
    return (
      <span className="step-icon-spin-wrapper" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" stroke="#e5e7eb" strokeWidth="2" fill="none" />
          <circle
            cx="10" cy="10" r="9"
            stroke="#f59e0b"
            strokeWidth="2"
            fill="none"
            strokeDasharray="20 38"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  // pending
  return (
    <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="9" stroke="#d1d5db" strokeWidth="2" fill="none" />
    </svg>
  );
}

// ── Header icon ──────────────────────────────────────────────────────────────
function HeaderIcon({ isRunning, hasFailures }) {
  if (isRunning) {
    return (
      <span className="exec-header-spinner" aria-hidden="true">
        <svg width="44" height="44" viewBox="0 0 44 44">
          <circle cx="22" cy="22" r="19" stroke="rgba(255,255,255,0.2)" strokeWidth="3" fill="none" />
          <circle
            cx="22" cy="22" r="19"
            stroke="white"
            strokeWidth="3"
            fill="none"
            strokeDasharray="80 40"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  if (hasFailures) {
    return (
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.15)" />
        <path d="M15 15L29 29M29 15L15 29" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.15)" />
      <path d="M13 22L19 28L31 16" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ExecutionProgress({ transactionNumber, employeeName, actionLog, isRunning }) {
  const stepStates = useMemo(() => parseStates(actionLog, isRunning), [actionLog, isRunning]);

  const completedCount = STEPS.filter((s) => stepStates[s.id] === "completed").length;
  const failedCount    = STEPS.filter((s) => stepStates[s.id] === "failed").length;
  const activeStep     = STEPS.find((s) => stepStates[s.id] === "in-progress");
  const progressPct    = Math.round(((completedCount + failedCount) / STEPS.length) * 100);

  return (
    <div className="exec-overlay">
      <div className="exec-modal">

        {/* Header */}
        <div className="exec-header">
          <HeaderIcon isRunning={isRunning} hasFailures={failedCount > 0} />
          <div className="exec-header-text">
            <h2>
              {isRunning
                ? "Processing Offboarding"
                : failedCount > 0
                ? "Completed with Errors"
                : "Offboarding Complete"}
            </h2>
            <p className="exec-txn">{transactionNumber}</p>
            {employeeName && <p className="exec-employee">{employeeName}</p>}
          </div>
        </div>

        {/* Body — checklist */}
        <div className="exec-body">
          {SECTIONS.map((section) => (
            <div key={section} className="exec-section">
              <div className="exec-section-title">{section}</div>
              {STEPS.filter((s) => s.section === section).map((step) => {
                const state = stepStates[step.id];
                return (
                  <div key={step.id} className={`exec-step exec-step-${state}`}>
                    <StepIcon state={state} />
                    <span className="exec-step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer — progress bar + status text */}
        <div className="exec-footer">
          <p className="exec-status-text">
            {isRunning
              ? activeStep
                ? `Running: ${activeStep.label}…`
                : "Initializing…"
              : `${completedCount} of ${STEPS.length} steps completed${failedCount > 0 ? ` · ${failedCount} failed` : ""} — Redirecting…`}
          </p>
          <div className="exec-progress-bar">
            <div className="exec-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

      </div>
    </div>
  );
}
